import React, { useEffect, useState, useCallback } from "react";
import Shell from "@/components/app/Shell";
import RequestCard from "@/components/app/RequestCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import CloseRequestButton from "@/components/app/CloseRequestButton";
import api, { formatApiError } from "@/lib/apiClient";
import { techWhatsAppLink, ROLE_LABEL } from "@/lib/status";
import MetricsPanel from "@/components/app/MetricsPanel";
import FinancialPanel from "@/components/app/FinancialPanel";
import NewRequestDialog from "@/components/app/NewRequestDialog";
import CreateCustomerDialog from "@/components/app/CreateCustomerDialog";
import EditStaffDialog from "@/components/app/EditStaffDialog";
import ResetPasswordDialog from "@/components/app/ResetPasswordDialog";
import FranchiseesTab from "@/components/app/FranchiseesTab";
import TechniciansTab from "@/components/app/TechniciansTab";
import ManufacturersTab from "@/components/app/ManufacturersTab";
import ContractsTab from "@/components/app/ContractsTab";
import QrBankTab from "@/components/app/QrBankTab";
import StaffEquipmentTab from "@/components/app/StaffEquipmentTab";
import CustomersTab from "@/components/app/CustomersTab";
import ServiceCatalogTab from "@/components/app/ServiceCatalogTab";
import ManagerOverview from "@/components/app/ManagerOverview";
import MobileTabBar from "@/components/app/MobileTabBar";
import { useAuth } from "@/context/AuthContext";
import ServiceOrderDialog from "@/components/app/ServiceOrderDialog";
import AgendaView from "@/components/app/AgendaView";
import { toast } from "sonner";
import { MessageCircle, UserPlus, Users, XCircle, Plus, Pencil, KeyRound, Store, AlertTriangle, FileText, CalendarClock, Home, ClipboardList, BarChart3, MoreHorizontal, DollarSign, Boxes, Wrench, Factory, FileSignature, BadgeCheck, RotateCcw } from "lucide-react";

function AssignControl({ request, technicians, franchisees, onChanged }) {
  const [techId, setTechId] = useState(request.assigned_technician_id || "");
  const [busy, setBusy] = useState(false);
  const [routing, setRouting] = useState(false);

  const redirect = async (franchisee_id) => {
    setRouting(true);
    try {
      await api.patch(`/requests/${request.id}/route`, { franchisee_id: franchisee_id || null });
      toast.success(franchisee_id ? "Chamado encaminhado ao franqueado." : "Chamado devolvido à sua fila.");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setRouting(false);
    }
  };

  const assign = async () => {
    if (!techId) { toast.error("Selecione um técnico."); return; }
    setBusy(true);
    try {
      await api.patch(`/requests/${request.id}/assign`, { technician_id: techId });
      toast.success("Técnico atribuído!");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    try {
      await api.patch(`/requests/${request.id}/cancel`, { reason: "Cancelado pelo gestor" });
      toast.success("Chamado cancelado.");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const reopen = async () => {
    try {
      await api.patch(`/requests/${request.id}/reopen`);
      toast.success("Chamado reaberto.");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const canCancel = !["concluido", "cancelado"].includes(request.status);
  const canReopen = request.status === "cancelado";

  if (canReopen) {
    return (
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button size="sm" variant="outline" className="w-full gap-1.5 border-blue-500 text-blue-700 hover:bg-blue-50"
            data-testid={`manager-reopen-button-${request.id}`}>
            <RotateCcw className="h-4 w-4" /> Reabrir chamado
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reabrir chamado?</AlertDialogTitle>
            <AlertDialogDescription>O chamado voltará a ficar ativo e o cliente será notificado.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction className="bg-blue-600 hover:bg-blue-700" onClick={reopen}
              data-testid={`manager-reopen-yes-${request.id}`}>Sim, reabrir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  return (
    <div className="space-y-2">
      {canCancel && <div className="flex gap-2">
        <Select value={techId} onValueChange={setTechId}>
          <SelectTrigger className="h-9" data-testid={`assign-select-${request.id}`}>
            <SelectValue placeholder="Atribuir técnico" />
          </SelectTrigger>
          <SelectContent>
            {technicians.map((t) => (
              <SelectItem key={t.id} value={t.id} data-testid={`tech-option-${t.id}`}>{t.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button size="sm" className="h-9 bg-blue-600 hover:bg-blue-700" onClick={assign}
          disabled={busy} data-testid={`assign-button-${request.id}`}>
          {request.assigned_technician_id ? "Reatribuir" : "Atribuir"}
        </Button>
      </div>}
      {request.assigned_technician_id && (
        <a href={techWhatsAppLink(request)} target="_blank" rel="noopener noreferrer"
          data-testid={`whatsapp-notify-${request.id}`}>
          <Button size="sm" variant="outline" className="w-full gap-1.5 border-green-500 text-green-700 hover:bg-green-50">
            <MessageCircle className="h-4 w-4" /> Notificar técnico no WhatsApp
          </Button>
        </a>
      )}
      {!["concluido", "cancelado"].includes(request.status) && (
        <div className="flex items-center gap-2">
          <Store className="h-4 w-4 text-slate-400 flex-shrink-0" />
          <Select value={request.franchisee_id || "__none__"} disabled={routing}
            onValueChange={(v) => redirect(v === "__none__" ? null : v)}>
            <SelectTrigger className="h-9" data-testid={`route-select-${request.id}`}>
              <SelectValue placeholder="Encaminhar a franqueado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__" data-testid={`route-none-${request.id}`}>Sem franqueado (gestor)</SelectItem>
              {franchisees.map((f) => (
                <SelectItem key={f.id} value={f.id} data-testid={`route-option-${f.id}`}>{f.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {canCancel && (
        <CloseRequestButton request={request} onChanged={onChanged} testPrefix="manager" />
      )}
      {canCancel && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button size="sm" variant="ghost" className="w-full gap-1.5 text-red-600 hover:bg-red-50"
              data-testid={`manager-cancel-button-${request.id}`}>
              <XCircle className="h-4 w-4" /> Cancelar chamado
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancelar chamado?</AlertDialogTitle>
              <AlertDialogDescription>O cliente será notificado do cancelamento.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Voltar</AlertDialogCancel>
              <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={cancel}
                data-testid={`manager-cancel-yes-${request.id}`}>Sim, cancelar</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}

function CreateStaffDialog({ franchisees, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "technician", franchisee_id: "" });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, franchisee_id: form.role === "technician" && form.franchisee_id ? form.franchisee_id : null };
      await api.post("/users", payload);
      toast.success("Usuário criado!");
      setForm({ name: "", email: "", phone: "", password: "", role: "technician", franchisee_id: "" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-blue-600 hover:bg-blue-700 gap-1.5" data-testid="add-staff-button">
          <UserPlus className="h-4 w-4" /> Adicionar técnico/gestor
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="create-staff-dialog">
        <DialogHeader><DialogTitle>Novo membro da equipe</DialogTitle>
          <DialogDescription>Cadastre um técnico ou gestor com e-mail e senha de acesso.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label>Função</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="staff-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="technician" data-testid="staff-role-technician">Técnico</SelectItem>
                <SelectItem value="manager" data-testid="staff-role-manager">Gestor</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Nome</Label>
            <Input required value={form.name} onChange={upd("name")} data-testid="staff-name-input" /></div>
          <div className="space-y-1.5"><Label>E-mail</Label>
            <Input type="email" required value={form.email} onChange={upd("email")} data-testid="staff-email-input" /></div>
          <div className="space-y-1.5"><Label>Telefone / WhatsApp</Label>
            <Input required value={form.phone} onChange={upd("phone")} placeholder="(21) 99999-9999" data-testid="staff-phone-input" /></div>
          {form.role === "technician" && (
            <div className="space-y-1.5">
              <Label>Franqueado (opcional)</Label>
              <Select value={form.franchisee_id || "__none__"} onValueChange={(v) => setForm({ ...form, franchisee_id: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="staff-franchisee-select"><SelectValue placeholder="Sem franqueado" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__" data-testid="staff-franchisee-none">Sem franqueado (direto do gestor)</SelectItem>
                  {franchisees.map((f) => (
                    <SelectItem key={f.id} value={f.id} data-testid={`staff-franchisee-${f.id}`}>{f.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5"><Label>Senha</Label>
            <Input type="password" required value={form.password} onChange={upd("password")} placeholder="Mínimo 6 caracteres" data-testid="staff-password-input" /></div>
          <DialogFooter>
            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={busy} data-testid="submit-staff-button">
              {busy ? "Salvando..." : "Criar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const FILTERS = [
  { v: "", label: "Todos" },
  { v: "aberto", label: "Abertos" },
  { v: "atribuido", label: "Atribuídos" },
  { v: "a_caminho", label: "A caminho" },
  { v: "concluido", label: "Concluídos" },
];

export default function ManagerDashboard() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [team, setTeam] = useState([]);
  const [franchisees, setFranchisees] = useState([]);
  const [filter, setFilter] = useState("");
  const [slaOnly, setSlaOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reqDialogOpen, setReqDialogOpen] = useState(false);
  const [editReq, setEditReq] = useState(null);
  const [editMember, setEditMember] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [osReq, setOsReq] = useState(null);
  const [tab, setTab] = useState("visao");

  const loadRequests = useCallback(async () => {
    const { data } = await api.get("/requests", { params: filter ? { status: filter } : {} });
    setRequests(data);
  }, [filter]);

  const loadTeam = useCallback(async () => {
    const { data } = await api.get("/users");
    setTeam(data);
    setTechnicians(data.filter((u) => u.role === "technician"));
  }, []);

  const loadFranchisees = useCallback(async () => {
    const { data } = await api.get("/franchisees");
    setFranchisees(data);
  }, []);

  useEffect(() => {
    (async () => { setLoading(true); await Promise.all([loadRequests(), loadTeam(), loadFranchisees()]); setLoading(false); })();
  }, [loadRequests, loadTeam, loadFranchisees]);

  const slaCount = requests.filter((r) => r.sla_breached).length;
  const visibleRequests = slaOnly ? requests.filter((r) => r.sla_breached) : requests;

  const openNew = () => { setEditReq(null); setReqDialogOpen(true); };
  const openEdit = (r) => { setEditReq(r); setReqDialogOpen(true); };

  const resetPassword = async (member) => {
    try {
      const { data } = await api.post(`/users/${member.id}/reset-password`);
      setResetResult(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <Shell title="Painel do Gestor" subtitle="Gerencie chamados e equipe"
      actions={
        <div className="flex gap-2">
          <CreateCustomerDialog />
          <Button className="bg-blue-600 hover:bg-blue-700 gap-1.5" onClick={openNew} data-testid="manager-new-request-button">
            <Plus className="h-4 w-4" /> Novo chamado
          </Button>
        </div>
      }>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="visao" data-testid="tab-visao">Visão Geral</TabsTrigger>
          <TabsTrigger value="chamados" data-testid="tab-chamados">Chamados</TabsTrigger>
          <TabsTrigger value="agenda" data-testid="tab-agenda">Agenda</TabsTrigger>
          <TabsTrigger value="financeiro" data-testid="tab-financeiro">Financeiro</TabsTrigger>
          <TabsTrigger value="metricas" data-testid="tab-metricas">Métricas</TabsTrigger>
          <TabsTrigger value="franqueados" data-testid="tab-franqueados">Franqueados</TabsTrigger>
          <TabsTrigger value="fabricantes" data-testid="tab-fabricantes">Fabricantes</TabsTrigger>
          <TabsTrigger value="contratos" data-testid="tab-contratos">Contratos</TabsTrigger>
          <TabsTrigger value="qrcodes" data-testid="tab-qrcodes">QR Codes</TabsTrigger>
          <TabsTrigger value="equipamentos" data-testid="tab-equipamentos">Equipamentos</TabsTrigger>
          <TabsTrigger value="clientes" data-testid="tab-clientes">Clientes</TabsTrigger>
          <TabsTrigger value="servicos" data-testid="tab-servicos">Serviços</TabsTrigger>
          <TabsTrigger value="credenciamento" data-testid="tab-credenciamento">Credenciamento</TabsTrigger>
          <TabsTrigger value="equipe" data-testid="tab-equipe">Equipe</TabsTrigger>
        </TabsList>

        <TabsContent value="visao">
          <ManagerOverview requests={requests} team={team} userName={user?.name}
            onQuick={setTab} onNew={openNew} />
        </TabsContent>

        <TabsContent value="chamados">
          <div className="flex gap-2 flex-wrap mb-4">
            {FILTERS.map((f) => (
              <Button key={f.v} size="sm" variant={filter === f.v ? "default" : "outline"}
                className={filter === f.v ? "bg-blue-600 hover:bg-blue-700" : ""}
                onClick={() => setFilter(f.v)} data-testid={`filter-${f.v || "all"}`}>
                {f.label}
              </Button>
            ))}
            <Button size="sm" variant={slaOnly ? "default" : "outline"}
              className={`gap-1.5 ${slaOnly ? "bg-red-600 hover:bg-red-700" : "text-red-600 border-red-200 hover:bg-red-50"}`}
              onClick={() => setSlaOnly((v) => !v)} data-testid="filter-sla">
              <AlertTriangle className="h-4 w-4" /> SLA {slaCount > 0 && `(${slaCount})`}
            </Button>
          </div>
          {loading ? (
            <p className="text-slate-400 text-sm">Carregando...</p>
          ) : visibleRequests.length === 0 ? (
            <p className="text-slate-500 py-10 text-center" data-testid="empty-requests">Nenhum chamado encontrado.</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2" data-testid="manager-requests-list">
              {visibleRequests.map((r) => (
                <RequestCard key={r.id} request={r} showCustomer
                  actions={
                    <div className="space-y-2">
                      <AssignControl request={r} technicians={technicians} franchisees={franchisees} onChanged={loadRequests} />
                      <Button size="sm" variant="outline" className="w-full gap-1.5"
                        onClick={() => openEdit(r)} data-testid={`edit-request-${r.id}`}>
                        <Pencil className="h-4 w-4" /> Editar chamado
                      </Button>
                      <Button size="sm" variant="outline" className="w-full gap-1.5 border-[#1e7be0] text-[#1e7be0] hover:bg-blue-50"
                        onClick={() => setOsReq(r)} data-testid={`service-order-${r.id}`}>
                        <FileText className="h-4 w-4" /> Ordem de Serviço
                      </Button>
                    </div>
                  } />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="metricas">
          <MetricsPanel />
        </TabsContent>

        <TabsContent value="agenda">
          <AgendaView requests={requests} technicians={technicians} showTechFilter
            onReschedule={async (id, date, time) => {
              try { await api.patch(`/requests/${id}/schedule`, { scheduled_date: date, scheduled_time: time }); toast.success("Chamado reagendado."); loadRequests(); }
              catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Falha ao reagendar."); }
            }} />
        </TabsContent>

        <TabsContent value="financeiro">
          <FinancialPanel />
        </TabsContent>

        <TabsContent value="franqueados">
          <FranchiseesTab franchisees={franchisees} reload={loadFranchisees} />
        </TabsContent>

        <TabsContent value="fabricantes">
          <ManufacturersTab />
        </TabsContent>

        <TabsContent value="contratos">
          <ContractsTab canCreate />
        </TabsContent>

        <TabsContent value="qrcodes">
          <QrBankTab />
        </TabsContent>

        <TabsContent value="equipamentos">
          <StaffEquipmentTab />
        </TabsContent>

        <TabsContent value="clientes">
          <CustomersTab />
        </TabsContent>

        <TabsContent value="servicos">
          <ServiceCatalogTab />
        </TabsContent>

        <TabsContent value="credenciamento">
          <TechniciansTab />
        </TabsContent>

        <TabsContent value="equipe">
          <div className="flex justify-end mb-4">
            <CreateStaffDialog franchisees={franchisees} onCreated={loadTeam} />
          </div>
          {team.length === 0 ? (
            <p className="text-slate-500 py-10 text-center">Nenhum membro cadastrado.</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2" data-testid="team-list">
              {team.map((u) => (
                <Card key={u.id} className="border-slate-200" data-testid={`team-member-${u.id}`}>
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center">
                        <Users className="h-5 w-5 text-slate-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-slate-800 truncate">{u.name}</p>
                        <p className="text-xs text-slate-500 truncate">{u.email} · {u.phone || "-"}</p>
                      </div>
                      <Badge variant="secondary">{ROLE_LABEL[u.role] || u.role}</Badge>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="flex-1 gap-1.5"
                        onClick={() => setEditMember(u)} data-testid={`edit-member-${u.id}`}>
                        <Pencil className="h-4 w-4" /> Editar
                      </Button>
                      <Button size="sm" variant="outline" className="flex-1 gap-1.5 text-amber-700 border-amber-200 hover:bg-amber-50"
                        onClick={() => resetPassword(u)} data-testid={`reset-password-${u.id}`}>
                        <KeyRound className="h-4 w-4" /> Senha provisória
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <NewRequestDialog open={reqDialogOpen}
        onOpenChange={(o) => { setReqDialogOpen(o); if (!o) setEditReq(null); }}
        onCreated={loadRequests} managerMode editRequest={editReq} />
      <EditStaffDialog open={!!editMember} onOpenChange={(o) => !o && setEditMember(null)}
        member={editMember} onSaved={loadTeam} />
      <ResetPasswordDialog open={!!resetResult} onOpenChange={(o) => !o && setResetResult(null)}
        result={resetResult} />
      <ServiceOrderDialog request={osReq} open={!!osReq} onOpenChange={(o) => !o && setOsReq(null)}
        onSaved={loadRequests} />

      <MobileTabBar items={[
        { key: "visao", label: "Início", icon: Home, onClick: () => setTab("visao"), active: tab === "visao" },
        { key: "chamados", label: "Chamados", icon: ClipboardList, onClick: () => setTab("chamados"), active: tab === "chamados" },
        { key: "equipe", label: "Equipe", icon: Users, onClick: () => setTab("equipe"), active: tab === "equipe" },
        { type: "fab", label: "Novo", icon: Plus, onClick: openNew },
        { key: "metricas", label: "Relatórios", icon: BarChart3, onClick: () => setTab("metricas"), active: tab === "metricas" },
        { type: "menu", key: "mais", label: "Mais", icon: MoreHorizontal, links: [
          { key: "agenda", label: "Agenda", icon: CalendarClock, onClick: () => setTab("agenda") },
          { key: "financeiro", label: "Financeiro", icon: DollarSign, onClick: () => setTab("financeiro") },
          { key: "clientes", label: "Clientes", icon: Users, onClick: () => setTab("clientes") },
          { key: "franqueados", label: "Franqueados", icon: Store, onClick: () => setTab("franqueados") },
          { key: "fabricantes", label: "Fabricantes", icon: Factory, onClick: () => setTab("fabricantes") },
          { key: "contratos", label: "Contratos", icon: FileSignature, onClick: () => setTab("contratos") },
          { key: "equipamentos", label: "Equipamentos", icon: Boxes, onClick: () => setTab("equipamentos") },
          { key: "qrcodes", label: "QR Codes", icon: MoreHorizontal, onClick: () => setTab("qrcodes") },
          { key: "servicos", label: "Serviços", icon: Wrench, onClick: () => setTab("servicos") },
          { key: "credenciamento", label: "Credenciamento", icon: BadgeCheck, onClick: () => setTab("credenciamento") },
        ] },
      ]} />
    </Shell>
  );
}
